import { useEffect } from 'react';

import { useDbtCloudAccount } from '@/context/DbtCloudProvider';

interface UseScrollToElementOptions {
  enabled?: boolean;
  delay?: number;
}

export const useScrollToElement = (
  ref: React.RefObject<HTMLElement>,
  options: UseScrollToElementOptions = {},
) => {
  const { hasAccountFeature } = useDbtCloudAccount();
  const { enabled = true, delay = 100 } = options;

  useEffect(() => {
    if (!enabled) return;

    const interval = setInterval(() => {
      if (!ref.current) return;
      const isAccountUI = hasAccountFeature('explorerAccountUI');

      if (isAccountUI) {
        // new account UI layout - scroll within main content area
        const scrollContainer =
          ref.current.closest('[class*="overflow-y-auto"]') ||
          document.querySelector('main') ||
          document.documentElement;

        const header = document.querySelector('header') as HTMLElement;
        const banner = document.querySelector(
          '[data-testid="missing-data-header"]',
        ) as HTMLElement;

        const headerHeight = header?.offsetHeight || 0;
        const bannerHeight = banner?.offsetHeight || 0;
        const totalOffset = headerHeight + bannerHeight + 40; // 40px padding

        // calculate the element's position relative to the scroll container
        const elementRect = ref.current.getBoundingClientRect();
        const containerRect = scrollContainer.getBoundingClientRect();

        // calculate the scroll position needed to show the element below all fixed elements
        const targetScrollTop =
          scrollContainer.scrollTop + elementRect.top - containerRect.top - totalOffset;

        scrollContainer.scrollTo({
          top: Math.max(0, targetScrollTop),
          behavior: 'auto',
        });
      } else {
        // legacy layout
        ref.current.scrollIntoView({ behavior: 'auto', block: 'center' });
      }

      clearInterval(interval);
    }, delay);

    return () => {
      clearInterval(interval);
    };
  }, [enabled, ref, hasAccountFeature, delay]);
};
